import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import api from './authApi'
import { authStatusUpdated, adBlockerStatusUpdated } from "./authSlice"
import { initTexts } from "../../store/translate/translate-actions"

export function AuthCallback() {
    useEffect(() => {
        if (!window.location.pathname.endsWith("/authenticated")) return
        if (window.opener) {
            window.opener.postMessage("AuthSucceeded", "*")
            window.self.close()
        }
    }, [])
    return null
}

/** Full URL the IdP should redirect to after login */
function getAuthNextUrl() {
    if (window.config?.authInPopup)
        return `${window.location.origin}/authenticated`
    
    return `${window.location.origin}${window.location.pathname}`
}

const AUTO_LOGIN_ATTEMPTED_KEY = "autoLoginAttempted"

function wasAutoLoginAttempted() {
    return sessionStorage.getItem(AUTO_LOGIN_ATTEMPTED_KEY) === "true"
}

function markAutoLoginAttempted() {
    sessionStorage.setItem(AUTO_LOGIN_ATTEMPTED_KEY, "true")
}

const useAuthService = () => {
    const dispatch = useDispatch()
    const { texts } = useSelector(state => state.translate)
    const buildAuthUrl = () =>
        `${window.config.baseApiUrl}/auth/eduidp?next=${encodeURIComponent(getAuthNextUrl())}`

    window.addEventListener("message", (event) => {
        if (event.data == "AuthSucceeded") {
            console.log("got message")
            auth()
        }
    }, false);

    const auth = () => {
        api.fetchAuthStatus()
            .then(({ isAuth, isAdmin, isStudent, user }) => {
                dispatch(authStatusUpdated({ isAuth, isAdmin, isStudent, user }))
                dispatch(initTexts())
            })
            .catch(e => {
                console.log(`couldn't authenticate: ${e}`);
                dispatch(authStatusUpdated({ isAuth: false, isAdmin: false, isStudent: false }))
                dispatch(initTexts())
            });
    };

    const startAuthentication = () => {
        const url = buildAuthUrl()
        const openInPopup = window.config?.authInPopup === true

        if (openInPopup) {
            const win = window.open(
                url,
                null,
                "popup=true,width=650,height=425,top=300,left=500"
            )

            if (win == null) {
                dispatch(adBlockerStatusUpdated({ isAdBlock: true }))
                return false
            }

            return true
        }

        window.location.assign(url)
        return true
    }

    const accessRequest = () => {
        if (window.config?.autoLogin === true) {
            if (wasAutoLoginAttempted()) return
            markAutoLoginAttempted()
        }

        document.requestStorageAccess()
            .then(() => startAuthentication())
            .catch((e) => console.log(e))
    }

    const adBlockerAuthentication = () => {
        const baseApiUrl = window.config?.baseApiUrl ?? null
        if (!baseApiUrl) return null
        let url = `${baseApiUrl}/auth/eduidp?next=${window.config?.botUrl}/authenticated`
        return (
            <div style={{
                display: "flex",
                height: "100%",
                width: "100%",
                backgroundColor: "var(--color-surface, #FFFFFF)",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
                position: "relative"
            }}>
                <div>{texts.adBlockerBlocksAuth}</div>
                <a href={url} target="_blank" rel="opener">{texts.clickHereToAuthenticate}</a>
            </div>
        )
    }

    return {
        auth: auth,
        startAuthentication: startAuthentication,
        accessRequest: accessRequest,
        adBlockerAuthentication: adBlockerAuthentication
    }
}

export default useAuthService
